import React from 'react';
import { clsx } from 'clsx';
import { AlertTriangle, CheckCircle2, Sparkles, XCircle } from 'lucide-react';
import { Button } from '@components/Button';
import { Dialog } from '@components/Dialog';
import type { ConnectionLevel, ConnectionReport } from '@/types/social';
import { OAUTH_NETWORKS } from '@/types/social';
import { formatDateTimeBR } from '@/utils/formatters';
import { NetworkIcon } from './SocialBits';

const LEVEL: Record<ConnectionLevel, { icon: React.ReactNode; box: string }> = {
  ok: { icon: <CheckCircle2 size={16} className="text-emerald-600" />, box: 'border-emerald-200 bg-emerald-50/50' },
  warning: { icon: <AlertTriangle size={16} className="text-amber-600" />, box: 'border-amber-200 bg-amber-50/60' },
  error: { icon: <XCircle size={16} className="text-red-600" />, box: 'border-red-200 bg-red-50/60' },
};

const ORDER: Record<ConnectionLevel, number> = { error: 0, warning: 1, ok: 2 };

interface ConnectionReportDialogProps {
  report: ConnectionReport | null;
  onClose: () => void;
}

/**
 * Diagnóstico do login na rede: o que veio (perfis conectados) e o que ficou de fora, com o
 * motivo e como corrigir. Documentação: src/docs/redes-sociais/contas.md.
 */
export const ConnectionReportDialog: React.FC<ConnectionReportDialogProps> = ({ report, onClose }) => {
  const label = OAUTH_NETWORKS.find((n) => n.key === report?.network)?.label ?? report?.network ?? '';
  const items = [...(report?.items ?? [])].sort((a, b) => ORDER[a.level] - ORDER[b.level]);
  return (
    <Dialog
      isOpen={!!report}
      onClose={onClose}
      title={`Diagnóstico da conexão — ${label}`}
      size="lg"
      footer={
        <div className="flex justify-end">
          <Button onClick={onClose}>Fechar</Button>
        </div>
      }
    >
      {report && (
        <div className="space-y-3">
          <p className="text-small text-slate-600">
            {report.created_at && `Login de ${formatDateTimeBR(report.created_at)}`}
            {report.created_by && ` por ${report.created_by}`}: <strong>{report.connected}</strong> conta(s) conectada(s)
            {report.warnings > 0 && (
              <>
                , <strong className="text-amber-700">{report.warnings}</strong> aviso(s)
              </>
            )}
            {report.errors > 0 && (
              <>
                , <strong className="text-red-700">{report.errors}</strong> problema(s)
              </>
            )}
            .
          </p>
          <ul className="space-y-2">
            {items.map((it, i) => (
              <li key={i} className={clsx('rounded-lg border p-2.5 text-sm space-y-1', LEVEL[it.level].box)}>
                <div className="flex items-start gap-2">
                  <span className="mt-0.5 flex-shrink-0">{LEVEL[it.level].icon}</span>
                  <span className="flex-1 min-w-0 font-medium text-slate-800 [overflow-wrap:anywhere]">
                    {it.network && <NetworkIcon network={it.network} size={13} className="inline mr-1 -mt-0.5" />}
                    {it.title}
                  </span>
                  {it.new && (
                    <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-600 px-1.5 text-[10px] font-semibold text-white">
                      <Sparkles size={10} /> nova
                    </span>
                  )}
                </div>
                {it.detail && <p className="pl-6 text-xs text-slate-600">{it.detail}</p>}
                {it.fix && (
                  <p className="pl-6 text-xs text-slate-700">
                    <strong>Como corrigir:</strong> {it.fix}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Dialog>
  );
};
